import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, CircularProgress, InputAdornment, Stack, Switch, TextField, Typography
} from '@mui/material';
import { ORGANIZATION_ASSIGNABLE_PLANS, SET_ORGANIZATION_PLANS } from 'api/graphql/organization';

// Constante de módulo: `planDiscounts` es dependencia de un useMemo, y un `[]` en la firma
// se recrearía en cada render rearmando el formulario.
const DEFAULT_PLAN_DISCOUNTS = [];

/**
 * Asocia planes a la organización con su % de descuento al cliente. El beneficio se auto-aplica
 * en el checkout a los miembros de la organización (sin cupón).
 */
function OrganizationPlansForm({
  organizationId,
  planDiscounts = DEFAULT_PLAN_DISCOUNTS,
  companyCovered = 0,
  onSaved = null
}) {
  const { t } = useTranslation(['organizations', 'buttons']);
  const { data, loading } = useQuery(ORGANIZATION_ASSIGNABLE_PLANS, { fetchPolicy: 'cache-and-network' });
  const [setOrganizationPlans, { loading: saving }] = useMutation(SET_ORGANIZATION_PLANS);
  const [msg, setMsg] = useState('');

  const initial = useMemo(() => {
    const map = {};
    (planDiscounts || []).forEach((pd) => {
      map[String(pd.plan_id)] = { included: true, discount: String(pd.client_discount_percent) };
    });
    return map;
  }, [planDiscounts]);

  const [rows, setRows] = useState(initial);

  // Firma del contenido guardado (no de la referencia): re-sincroniza los toggles cuando
  // `planDiscounts` llega tarde o cambia tras guardar, SIN pisar lo que estés editando.
  const savedSignature = useMemo(
    () => (planDiscounts || [])
      .map((pd) => `${pd.plan_id}:${pd.client_discount_percent}:${pd.is_active}`)
      .sort()
      .join('|'),
    [planDiscounts]
  );
  // Solo cuando cambia el contenido guardado (no en cada respuesta de red con la misma data),
  // para no pisar lo que estés editando. `initial` se recalcula con el mismo planDiscounts.
  useEffect(() => {
    setRows(initial);
  }, [savedSignature]);

  const plans = data?.plans?.data ?? [];

  const getRow = (planId) => rows[String(planId)] || { included: false, discount: '0' };
  const setRow = (planId, patch) => setRows((prev) => ({ ...prev, [String(planId)]: { ...getRow(planId), ...patch } }));

  // Al activar un plan sin % previo, se precarga con lo que la empresa cubre por defecto.
  const toggleIncluded = (planId, checked) => {
    const current = getRow(planId);
    const patch = { included: checked };
    if (checked && (!current.discount || current.discount === '0')) {
      patch.discount = String(companyCovered || 0);
    }
    setRow(planId, patch);
  };

  const handleSave = async () => {
    const payload = plans
      .filter((p) => getRow(p.id).included)
      .map((p) => ({
        plan_id: p.id,
        client_discount_percent: Math.max(0, Math.min(100, parseInt(getRow(p.id).discount, 10) || 0)),
        is_active: true
      }));
    await setOrganizationPlans({ variables: { organization_id: organizationId, plans: payload } });
    setMsg(t('plansSaved', { defaultValue: 'Planes y descuentos guardados.' }));
    if (onSaved) onSaved();
  };

  if (loading && plans.length === 0) {
    return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: 'gold.main' }} /></Box>;
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t('plansHelpCovered', { defaultValue: 'Elige los planes que vende esta organización y cuánto cubre la empresa en cada uno. El miembro paga el resto en el checkout. 100% = la empresa paga todo (otórgalo en Miembros).' })}
      </Typography>

      <Stack spacing={1.25} sx={{ mb: 2 }}>
        {plans.map((p) => {
          const row = getRow(p.id);
          return (
            <Stack
              key={p.id}
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.content' }}
            >
              <Switch checked={row.included} onChange={(e) => toggleIncluded(p.id, e.target.checked)} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{p.name}</Typography>
                {!p.is_subscribable && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('notSubscribable', { defaultValue: 'Sin suscripción mensual: el miembro lo paga como pago único (el descuento se aplica igual).' })}
                  </Typography>
                )}
              </Box>
              <TextField
                size="small"
                type="number"
                value={row.discount}
                onChange={(e) => setRow(p.id, { discount: e.target.value })}
                disabled={!row.included}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, inputProps: { min: 0, max: 100 } }}
                sx={{ width: 110 }}
              />
            </Stack>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {t('save', { ns: 'buttons', defaultValue: 'Guardar' })}
        </Button>
        {msg && <Typography variant="caption" sx={{ color: 'var(--otx-gold-text)' }}>{msg}</Typography>}
      </Stack>
    </Box>
  );
}

OrganizationPlansForm.propTypes = {
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  planDiscounts: PropTypes.arrayOf(PropTypes.shape({
    plan_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    client_discount_percent: PropTypes.number
  })),
  companyCovered: PropTypes.number,
  onSaved: PropTypes.func
};

export default OrganizationPlansForm;
